import Box from '@mui/material/Box';
import Input from '@mui/material/Input';
import { styled } from '@mui/material/styles';
import React, { ChangeEvent, ClipboardEvent, KeyboardEvent, useCallback, useRef } from 'react';

const OTP_LENGTH_DEFAULT = 4;

const DigitInput = styled(Input)(({ theme }) => ({
    '& input': {
        padding: theme.spacing(1),
        textAlign: 'center',
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        width: theme.spacing(4.25),
        height: theme.spacing(4.25),
        fontSize: theme.typography.pxToRem(18),
        fontWeight: theme.typography.fontWeightBold,
        boxSizing: 'border-box'
    }
}));

export interface OtpCodeInputProps {
    value: string;
    onChange: (next: string) => void;
    length?: number;
    disabled?: boolean;
    idPrefix?: string;
}

function charsForValue(value: string, length: number): string[] {
    return Array.from({ length }, (_, i) => value[i] ?? '');
}

function mergeOtpChars(chars: string[], length: number): string {
    return chars.join('').slice(0, length);
}

const OtpCodeInput: React.FC<OtpCodeInputProps> = ({ value, onChange, length = OTP_LENGTH_DEFAULT, disabled, idPrefix = 'otp-digit' }) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const setRef = useCallback((index: number, el: HTMLInputElement | null) => {
        inputRefs.current[index] = el;
    }, []);

    const focusCell = useCallback((index: number) => {
        const el = inputRefs.current[index];
        if (el) {
            el.focus();
            el.select();
        }
    }, []);

    const applyChars = useCallback(
        (nextChars: string[]) => {
            onChange(mergeOtpChars(nextChars, length));
        },
        [length, onChange]
    );

    const handleChange = useCallback(
        (index: number, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const sanitized = event.target.value.replace(/\D/g, '');
            const chars = charsForValue(value, length);
            const digit = sanitized.slice(-1);
            if (digit) {
                chars[index] = digit;
                applyChars(chars);
                if (index < length - 1) {
                    requestAnimationFrame(() => focusCell(index + 1));
                }
            } else {
                chars[index] = '';
                applyChars(chars);
            }
        },
        [applyChars, focusCell, length, value]
    );

    const handlePaste = useCallback(
        (event: ClipboardEvent<HTMLDivElement>) => {
            const text = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
            if (!text) return;
            event.preventDefault();
            const chars = charsForValue(value, length);
            for (let i = 0; i < text.length; i += 1) {
                chars[i] = text[i] ?? '';
            }
            applyChars(chars);
            requestAnimationFrame(() => focusCell(Math.min(text.length, length) - 1));
        },
        [applyChars, focusCell, length, value]
    );

    const handleKeyDown = useCallback(
        (index: number, event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (event.key === 'Backspace') {
                const chars = charsForValue(value, length);
                if (!chars[index] && index > 0) {
                    event.preventDefault();
                    chars[index - 1] = '';
                    applyChars(chars);
                    focusCell(index - 1);
                } else if (chars[index]) {
                    chars[index] = '';
                    applyChars(chars);
                }
            }
            if (event.key === 'ArrowLeft' && index > 0) {
                event.preventDefault();
                focusCell(index - 1);
            }
            if (event.key === 'ArrowRight' && index < length - 1) {
                event.preventDefault();
                focusCell(index + 1);
            }
        },
        [applyChars, focusCell, length, value]
    );

    const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if ('select' in event.target && typeof event.target.select === 'function') {
            event.target.select();
        }
    }, []);

    return (
        <Box
            component="div"
            role="group"
            aria-label="One-time code"
            onPasteCapture={handlePaste}
            sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}
        >
            {Array.from({ length }).map((_, index) => (
                <DigitInput
                    key={index}
                    id={`${idPrefix}-${index}`}
                    disabled={disabled}
                    disableUnderline
                    size="small"
                    value={value[index] ?? ''}
                    onChange={(e) => handleChange(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onFocus={handleFocus}
                    inputProps={{
                        maxLength: 1,
                        ref: (el: HTMLInputElement | null) => setRef(index, el),
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        autoComplete: index === 0 ? 'one-time-code' : 'off',
                        'aria-label': `Digit ${index + 1} of ${length}`
                    }}
                />
            ))}
        </Box>
    );
};

export default React.memo(OtpCodeInput);
